import * as THREE from 'three'

const vertShader = /* glsl */`
  varying vec3 vDir;
  void main() {
    // Pass the local (model-space) vertex position as direction.
    // BackSide sphere — camera is always inside, so vDir spans the whole sky.
    vDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const fragShader = /* glsl */`
  uniform float time;
  varying vec3 vDir;

  // ── Noise helpers (sin-free hash — much faster) ───────────────────────────
  float hash(vec2 p) {
    p = fract(p * vec2(0.1031, 0.1030));
    p += dot(p, p.yx + 33.33);
    return fract((p.x + p.y) * p.x);
  }

  float smoothNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),             hash(i + vec2(1,0)), f.x),
      mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), f.x),
      f.y
    );
  }

  // 3 octaves only, no matrix rotation — ~3× cheaper than before
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    v += a * smoothNoise(p); p = p * 2.1 + vec2(1.7, 9.2); a *= 0.5;
    v += a * smoothNoise(p); p = p * 2.1 + vec2(8.3, 2.8); a *= 0.5;
    v += a * smoothNoise(p);
    return v;
  }

  void main() {
    // Use 3D direction slices — NO atan, NO seam
    vec3  n  = normalize(vDir);
    float t  = time * 0.035;

    // 3 FBM calls using different axis pairs — each covers a different
    // degenerate direction so no camera angle ever gets a fully black sky.
    // n.xz — good for y-axis viewing; degenerates at n=(0,0,±1)
    // n.xy — good for z-axis viewing; degenerates at n=(0,±1,0)
    // n.yz — good for x-axis viewing; covers n=(0,0,-1) (VFX/Contact camera)
    float n1 = fbm(n.xz * 2.2 + vec2( t,        t * 0.55));
    float n2 = fbm(n.xy * 3.8 - vec2( t * 0.7,  t * 1.15));
    float n4 = fbm(n.yz * 2.6 + vec2( t * 0.5,  t * 0.85));  // covers VFX direction
    float n3 = n1 * (1.0 - n2);   // free cross-layer pattern, no extra FBM

    // ── Pastel dawn: vertical gradient sky with depth ───────────────────
    // periwinkle top → bright soft middle → warm peach low. HDR values
    // tuned for ACES; everything stays below the bloom threshold (2.5).
    // The camera only ever sees n.y in roughly ±0.4, so the gradient
    // transitions are compressed into that band to stay visible on screen.
    vec3 topCol = vec3(0.82, 0.92, 1.48);   // periwinkle
    vec3 midCol = vec3(1.42, 1.35, 1.40);   // bright soft centre
    vec3 lowCol = vec3(1.58, 1.08, 0.86);   // warm peach horizon

    float h  = n.y;
    vec3 col = mix(lowCol, midCol, smoothstep(-0.40, 0.00, h));
    col      = mix(col,    topCol, smoothstep(0.02, 0.42, h));

    // Soft warm sun halo — upper right, sits behind the hero model
    vec3 sunDir = normalize(vec3(0.55, 0.45, -0.70));
    col += vec3(0.62, 0.38, 0.18) * pow(max(dot(n, sunDir), 0.0), 4.0);

    // Cloud thresholds — looser than dark theme so wisps read on light
    float cloud1 = pow(max(n1 - 0.40, 0.0) * 1.90, 2.6);   // sparse wisps
    float cloud2 = pow(max(n2 - 0.44, 0.0) * 1.92, 3.0);   // deep pockets
    float cloud3 = pow(max(n3 - 0.46, 0.0) * 2.00, 2.4);   // faint accent
    float cloud4 = pow(max(n4 - 0.38, 0.0) * 1.75, 2.2);   // wide-angle layer

    // Pastel clouds — stronger than before so the sky doesn't read flat
    col = mix(col, vec3(0.80, 0.74, 1.15), clamp(cloud1 * 0.95, 0.0, 1.0));  // lavender wisps
    col = mix(col, vec3(1.25, 0.85, 0.95), clamp(cloud2 * 0.75, 0.0, 1.0));  // soft rose
    col = mix(col, vec3(1.35, 1.00, 0.80), clamp(cloud3 * 0.65, 0.0, 1.0));  // peach accent
    col = mix(col, vec3(0.88, 0.82, 1.18), clamp(cloud4 * 0.85, 0.0, 1.0));  // mauve layer

    // Soft core overlap — deeper lavender pocket
    float core = pow(cloud1 * cloud2, 0.6);
    col = mix(col, vec3(0.70, 0.64, 1.05), clamp(core * 0.55, 0.0, 1.0));

    gl_FragColor = vec4(col, 1.0);
  }
`

export function buildSkyDome(scene) {
  // Large sphere — camera is always well inside (scene models sit at z ≈ 0-6)
  const geo = new THREE.SphereGeometry(48, 40, 40)
  const mat = new THREE.ShaderMaterial({
    uniforms:      { time: { value: 0 } },
    vertexShader:  vertShader,
    fragmentShader: fragShader,
    side:          THREE.BackSide,
    depthWrite:    false,
  })

  const mesh = new THREE.Mesh(geo, mat)
  mesh.renderOrder = -1   // render before everything else
  scene.add(mesh)

  // Clear any old static texture/colour — sky dome handles it all
  scene.background = null

  return {
    mesh,
    update(elapsed) { mat.uniforms.time.value = elapsed }
  }
}
